import { toast } from "sonner";

// Re-export Station type from server schema
export type Station = {
	id: string;
	name: string;
	slug: string;
	description?: string;
	broadcasterId: string;
	broadcaster: {
		id: string;
		name: string;
		username: string;
		image?: string;
	};
	roomId?: string;
	isActive: boolean;
	viewerCount: number;
	status?: "live" | "disconnected" | "ended";
	nowPlaying: {
		artist?: string;
		track?: string;
		album?: string;
		artworkUrl?: string;
		updatedAt?: string;
	} | null;
	createdAt: string;
	lastActiveAt: string;
	hostDisconnectedAt?: string;
};

// API configuration
const getApiUrl = () => {
	// In development, use the same host as the app
	if ((import.meta as any).env.DEV) {
		return "http://localhost:4040";
	}
	// In production, use the full URL or environment variable
	return (import.meta as any).env.VITE_API_URL || "";
};

// Browser-compatible API client using fetch
const makeApiCall = async (endpoint: string, options?: RequestInit) => {
	const url = `${getApiUrl()}${endpoint}`;
	const response = await fetch(url, {
		credentials: 'include',
		headers: {
			"Content-Type": "application/json",
			...options?.headers,
		},
		...options,
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(
			`API call failed: ${response.status} ${response.statusText} - ${errorText}`,
		);
	}

	return response.json();
};

// Export a promise-based API client for React components
export const apiClient = {
	// Station endpoints
	stations: {
		get: (stationId: string) => makeApiCall(`/api/stations/${stationId}`),

		listActive: async () => {
			const response = await makeApiCall("/api/stations/discover");
			// Extract stations array from the response
			return response.stations;
		},

		create: (data: { name: string; description?: string }) =>
			makeApiCall("/api/stations", {
				method: "POST",
				body: JSON.stringify(data),
			}),

		update: (data: {
			stationId: string;
			name?: string;
			description?: string;
		}) =>
			makeApiCall("/api/stations/update", {
				method: "POST",
				body: JSON.stringify(data),
			}),

		updateNowPlaying: (data: { stationId: string; nowPlaying?: any }) =>
			makeApiCall("/api/stations/now-playing", {
				method: "POST",
				body: JSON.stringify(data),
			}),

		clearNowPlaying: (stationId: string) =>
			makeApiCall("/api/stations/now-playing/clear", {
				method: "POST",
				body: JSON.stringify({ stationId }),
			}),

		end: (stationId: string) =>
			makeApiCall("/api/stations/end", {
				method: "POST",
				body: JSON.stringify({ stationId }),
			}),

		joinAsReceiver: (data: { stationId: string; receiverName?: string }) =>
			makeApiCall("/api/joinStationAsReceiver", {
				method: "POST",
				body: JSON.stringify(data),
			}),

		refreshToken: (data: { stationId: string }) =>
			makeApiCall("/api/stations/token/refresh", {
				method: "POST",
				body: JSON.stringify(data),
			}),
	},

	// User endpoints (new)
	users: {
		getStationsByUsername: (username: string) =>
			makeApiCall(`/api/users/${username}/stations`),

		getStationByUsernameAndSlug: (username: string, slug: string) =>
			makeApiCall(`/api/users/${username}/stations/${slug}`),
	},

	// Domain endpoints (for CORS whitelisting)
	domains: {
		list: () => makeApiCall("/api/me/allowed-domains"),

		add: (domain: string) =>
			makeApiCall("/api/me/allowed-domains", {
				method: "POST",
				body: JSON.stringify({ domain }),
			}),

		delete: (domainId: string) =>
			makeApiCall(`/api/me/allowed-domains/${domainId}`, {
				method: "DELETE",
			}),
	},

	// Checkout endpoints
	checkout: {
		createSession: (data?: { couponCode?: string }) =>
			makeApiCall("/api/checkout/create-session", {
				method: "POST",
				body: JSON.stringify(data || {}),
			}),

		verifySession: (sessionId: string) =>
			makeApiCall("/api/checkout/verify-session", {
				method: "POST",
				body: JSON.stringify({ sessionId }),
			}),

		redeem: (code: string) =>
			makeApiCall("/api/checkout/redeem", {
				method: "POST",
				body: JSON.stringify({ code }),
			}),
	},

	// Health check
	health: () => makeApiCall("/health"),
};

// Error handling helpers
export function handleApiError(error: unknown, fallbackMessage?: string) {
	// Effect errors will be wrapped in different structure
	let message = "An unexpected error occurred";
	let code = "UNKNOWN_ERROR";

	if (error instanceof Error) {
		message = error.message;
		// Try to extract error code from Effect error
		if ("_tag" in error) {
			code = (error as any)._tag;
		}
	}

	// Handle specific error codes
	switch (code) {
		case "StationNotFoundError":
			toast.error("Station not found", {
				description: "This station may have been deleted or doesn't exist.",
			});
			break;
		case "STATION_NOT_ACTIVE":
			toast.error("Station offline", {
				description: "This station is not currently broadcasting.",
			});
			break;
		case "NETWORK_ERROR":
			toast.error("Connection failed", {
				description: "Please check your internet connection and try again.",
			});
			break;
		case "Unauthorized":
			toast.error("Authentication required", {
				description: "Please log in to perform this action.",
			});
			break;
		default:
			toast.error(fallbackMessage || "An error occurred", {
				description: message,
			});
	}
}

// Polling helper for real-time updates
export function createPollingSubscription<T>(
	fetcher: () => Promise<T>,
	onData: (data: T) => void,
	options: {
		interval?: number;
		onError?: (error: unknown) => void;
		enabled?: boolean;
	} = {},
) {
	const { interval = 5000, onError, enabled = true } = options;

	let timeoutId: NodeJS.Timeout | null = null;
	let isActive = enabled;

	const poll = async () => {
		if (!isActive) {
			return;
		}

		try {
			const data = await fetcher();
			onData(data);
		} catch (error) {
			onError?.(error);
		}

		if (isActive) {
			timeoutId = setTimeout(poll, interval);
		}
	};

	// Start polling if enabled
	if (enabled) {
		poll();
	}

	// Return cleanup function
	return {
		start: () => {
			isActive = true;
			poll();
		},
		stop: () => {
			isActive = false;
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		},
		setEnabled: (value: boolean) => {
			if (value && !isActive) {
				isActive = true;
				poll();
			} else if (!value && isActive) {
				isActive = false;
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = null;
				}
			}
		},
	};
}

// Export the raw API call function for advanced usage
export const rawApiCall = makeApiCall;
